// ============================================================
// components/ResultsPanel.jsx
// Panel de resultados: muestra el desglose completo del costo
// con fórmulas explicadas y precio final destacado
// ============================================================

import React from 'react';
import { ResultCell, NeonDivider, FormulaDisplay, Badge } from './UI';

/**
 * Panel completo de resultados del cálculo.
 * Muestra cada componente de costo con su fórmula correspondiente.
 *
 * @param {Object}   results         - Objeto de resultados de calculateTotalCost()
 * @param {Function} fmt             - Función de formateo de moneda
 * @param {Object}   selectedPrinter - Impresora actualmente seleccionada
 * @param {Object}   currency        - Moneda seleccionada
 * @param {Object}   form            - Valores del formulario para mostrar en fórmulas
 * @param {number}   totalHours      - Horas totales de impresión
 */
export function ResultsPanel({
  results,
  fmt,
  selectedPrinter,
  currency,
  form,
  totalHours,
}) {
  const {
    powerKw,
    energyConsumedKwh,
    electricityCost,
    materialCost,
    maintenanceCost,
    depreciationCost,
    laborCost,
    additionalTotal,
    subtotal,
    profitAmount,
    finalPrice,
    costPerGram,
    marginPercent,
  } = results;

  // Nombre de la impresora para mostrar en el header
  const printerName = selectedPrinter.id === 'custom'
    ? `Custom (${form.customPowerWatts}W)`
    : `${selectedPrinter.brand} ${selectedPrinter.model}`;

  // Horas formateadas como "Xh Ym"
  const h = Math.floor(totalHours);
  const m = Math.round((totalHours - h) * 60);
  const timeFormatted = m > 0 ? `${h}h ${m}m` : `${h}h`;

  return (
    <div style={{
      background: 'var(--bg-card)',
      border: '1px solid rgba(57, 255, 20, 0.2)',
      borderLeft: '3px solid var(--neon-green)',
      borderRadius: 'var(--radius-lg)',
      padding: '28px',
      animation: 'fadeInUp 0.5s ease both',
    }}>
      {/* ---- Encabezado del panel ---- */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: '24px',
        flexWrap: 'wrap',
        gap: '12px',
      }}>
        <div>
          <h2 style={{
            fontFamily: 'var(--font-display)',
            fontSize: '1rem',
            fontWeight: '900',
            color: 'var(--neon-green)',
            letterSpacing: '0.15em',
            textTransform: 'uppercase',
            textShadow: 'var(--glow-green)',
            margin: 0,
          }}>
            Desglose de Costos
          </h2>
          <p style={{
            color: 'var(--text-secondary)',
            fontSize: '0.78rem',
            marginTop: '4px',
            fontFamily: 'var(--font-mono)',
          }}>
            {printerName} &nbsp;•&nbsp; {timeFormatted} &nbsp;•&nbsp;
            {form.usedGrams}g {form.filamentType?.toUpperCase()}
          </p>
        </div>
        <Badge color="green">Calculado</Badge>
      </div>

      {/* ---- Bloque 1: Electricidad ---- */}
      <BlockTitle>1. Costo de Electricidad</BlockTitle>
      <FormulaDisplay
        formula={`${powerKw.toFixed(3)} kW × ${totalHours.toFixed(2)} h × ${fmt(parseFloat(form.kwPrice))}/kWh`}
        description="Potencia (kW) × Duración (h) × Precio del kWh"
      />
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '12px', margin: '12px 0 20px' }}>
        <ResultCell
          label="Potencia"
          value={`${powerKw.toFixed(3)} kW`}
          formula={`${form.customPowerWatts || selectedPrinter.powerWatts}W ÷ 1000`}
        />
        <ResultCell
          label="Energía total"
          value={`${energyConsumedKwh.toFixed(3)} kWh`}
          formula={`${powerKw.toFixed(3)} × ${totalHours.toFixed(2)}h`}
        />
        <ResultCell
          label="Costo Electricidad"
          value={fmt(electricityCost)}
          formula={`${energyConsumedKwh.toFixed(3)} × ${fmt(parseFloat(form.kwPrice))}`}
        />
      </div>

      <NeonDivider />

      {/* ---- Bloque 2: Material ---- */}
      <BlockTitle>2. Costo de Material</BlockTitle>
      <FormulaDisplay
        formula={`(${fmt(parseFloat(form.filamentPricePerKg))} / 1000g) × ${form.usedGrams}g`}
        description="Precio por gramo × gramos utilizados"
      />
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '12px', margin: '12px 0 20px' }}>
        <ResultCell
          label="Precio/gramo"
          value={fmt(parseFloat(form.filamentPricePerKg) / 1000)}
          formula={`${fmt(parseFloat(form.filamentPricePerKg))} ÷ 1000`}
        />
        <ResultCell
          label="Gramos usados"
          value={`${parseFloat(form.usedGrams).toFixed(2)} g`}
        />
        <ResultCell
          label="Costo Material"
          value={fmt(materialCost)}
          formula={`precio/g × ${form.usedGrams}g`}
        />
      </div>

      <NeonDivider />

      {/* ---- Bloque 3: Costos adicionales ---- */}
      <BlockTitle>3. Costos Adicionales</BlockTitle>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '12px', margin: '12px 0 20px' }}>
        {maintenanceCost > 0 && (
          <ResultCell
            label="Mantenimiento"
            value={fmt(maintenanceCost)}
            formula={`${fmt(parseFloat(form.maintenancePerHour))}/h × ${totalHours.toFixed(2)}h`}
          />
        )}
        {depreciationCost > 0 && (
          <ResultCell
            label="Depreciación"
            value={fmt(depreciationCost)}
            formula={`${fmt(parseFloat(form.depreciationPerHour))}/h × ${totalHours.toFixed(2)}h`}
          />
        )}
        {laborCost > 0 && (
          <ResultCell
            label="Mano de obra"
            value={fmt(laborCost)}
          />
        )}
        {additionalTotal === 0 && (
          <div style={{
            color: 'var(--text-secondary)',
            fontSize: '0.8rem',
            fontFamily: 'var(--font-mono)',
            padding: '10px',
            gridColumn: '1 / -1',
          }}>
            No se ingresaron costos adicionales
          </div>
        )}
        {additionalTotal > 0 && (
          <ResultCell
            label="Total adicionales"
            value={fmt(additionalTotal)}
          />
        )}
      </div>

      <NeonDivider color="var(--neon-orange)" />

      {/* ---- Bloque 4: Resumen y precio final ---- */}
      <BlockTitle accent>4. Resumen y Precio Final</BlockTitle>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(170px, 1fr))', gap: '12px', margin: '12px 0' }}>
        <ResultCell
          label="Subtotal (sin margen)"
          value={fmt(subtotal)}
          formula={`Luz + Material + Adicionales`}
        />
        <ResultCell
          label={`Ganancia (${marginPercent}%)`}
          value={fmt(profitAmount)}
          formula={`${fmt(subtotal)} × ${marginPercent}%`}
        />
        <ResultCell
          label="Costo por gramo"
          value={fmt(costPerGram)}
          formula={`Precio final ÷ ${form.usedGrams}g`}
        />
      </div>

      {/* ---- Precio final: elemento destacado ---- */}
      <div style={{
        background: 'linear-gradient(135deg, rgba(255, 107, 0, 0.15), rgba(255, 229, 0, 0.05))',
        border: '2px solid rgba(255, 107, 0, 0.5)',
        borderRadius: 'var(--radius-lg)',
        padding: '24px',
        textAlign: 'center',
        marginTop: '20px',
        position: 'relative',
        overflow: 'hidden',
      }}>
        {/* Glow decorativo ---- */}
        <div style={{
          position: 'absolute',
          top: '-40px',
          left: '50%',
          transform: 'translateX(-50%)',
          width: '200px',
          height: '80px',
          background: 'radial-gradient(ellipse, rgba(255, 107, 0, 0.3), transparent 70%)',
          pointerEvents: 'none',
        }} />

        <p style={{
          fontFamily: 'var(--font-display)',
          fontSize: '0.75rem',
          letterSpacing: '0.2em',
          color: 'var(--text-secondary)',
          textTransform: 'uppercase',
          marginBottom: '8px',
        }}>
          Precio Final de Venta
        </p>
        <p style={{
          fontFamily: 'var(--font-display)',
          fontSize: '2.8rem',
          fontWeight: '900',
          color: 'var(--neon-orange)',
          textShadow: 'var(--glow-orange)',
          letterSpacing: '0.05em',
          lineHeight: 1,
          margin: '8px 0',
        }}>
          {fmt(finalPrice)}
        </p>
        <p style={{
          fontFamily: 'var(--font-mono)',
          fontSize: '0.75rem',
          color: 'var(--text-secondary)',
          marginTop: '8px',
        }}>
          {currency.code} &nbsp;•&nbsp; Margen {marginPercent}% &nbsp;•&nbsp;
          Ganancia neta: {fmt(profitAmount)}
        </p>

        {/* Barra de progreso del margen ---- */}
        <div style={{
          marginTop: '16px',
          background: 'rgba(0,0,0,0.3)',
          borderRadius: '100px',
          height: '6px',
          overflow: 'hidden',
        }}>
          <div style={{
            width: `${Math.min((profitAmount / finalPrice) * 100, 100)}%`,
            height: '100%',
            background: 'linear-gradient(90deg, var(--neon-orange), var(--neon-yellow))',
            borderRadius: '100px',
            transition: 'width 0.6s ease',
          }} />
        </div>
        <p style={{
          fontFamily: 'var(--font-mono)',
          fontSize: '0.65rem',
          color: 'rgba(136,136,168,0.6)',
          marginTop: '6px',
        }}>
          {((profitAmount / finalPrice) * 100).toFixed(1)}% del precio final es ganancia
        </p>
      </div>
    </div>
  );
}

// ---- Sub-componente: Título de bloque ----
function BlockTitle({ children, accent = false }) {
  return (
    <h4 style={{
      fontFamily: 'var(--font-display)',
      fontSize: '0.72rem',
      fontWeight: '700',
      letterSpacing: '0.12em',
      textTransform: 'uppercase',
      color: accent ? 'var(--neon-orange)' : 'var(--neon-cyan)',
      marginBottom: '10px',
      marginTop: '4px',
    }}>
      {children}
    </h4>
  );
}
